"use client";

import { useQuery } from "@tanstack/react-query";
import { isEqual } from "es-toolkit";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import type {
  FocusNotification,
  GetNotificationParams,
  NotificationSubCategory,
} from "@/backend/focus";
import {
  getNotificationsRequest,
  NotificationOrderBy,
  NotificationStatus,
} from "@/backend/focus";
import { NotificationOrderBySortKey } from "@/components/shared/filter-sections";
import type {
  FilterNumericValue,
  FilterRecord,
  FiltersSortContextType,
  SortVariant,
} from "@/contexts/filters-sort-context";
import { useFiltersAndSort } from "@/contexts/filters-sort-context";
import { UserContext } from "@/contexts/user-context";
import { useFocusPreferences } from "@/hooks/use-focus-preferences";
import { usePaginationRequest } from "@/hooks-api/use-pagination-request";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import {
  areNotificationViewRefsEqual,
  createBuiltinNotificationViewRef,
  getNotificationViewHiddenTypes,
  notificationMatchesUnreadView,
  type NotificationViewRef,
  type NotificationViewResolution,
  parseNotificationViewRef,
  resolveNotificationViewItem,
  resolveNotificationViewItems,
  serializeNotificationViewRef,
} from "@/utils/notification-views";

import { mergeNotificationsById } from "./notification-list-utils";

export type NotificationsContextType = {
  lazyLoadingMarkup: ReactNode;
  notifications: FocusNotification[];
  onFiltersApply: () => void;
  resetAll: () => void;
  initialLoading: boolean;
  resetNotificationFilters: () => void;
  resetNotificationSort: () => void;
  setNotifications: Dispatch<SetStateAction<FocusNotification[]>>;
  notificationPublicKey: string;
  markUnreadNotificationsAsRead: boolean;
  showUnreadIndicator: boolean;
  relativeUsername: string | null;
  activeNotificationView: NotificationViewResolution | null;
  decrementNotificationViewUnreadCounts: (
    notification: FocusNotification,
  ) => void;
  markAllNotificationViewCountsAsRead: () => void;
  notificationViewItems: NotificationViewResolution[];
  notificationViewUnreadCounts: Record<string, number>;
  notificationViewsEnabled: boolean;
  refreshNotificationViewCounts: () => void;
  refreshNotifications: () => void;
  hideNotificationType: (
    notificationType: NotificationSubCategory,
  ) => Promise<void>;
  hiddenNotificationTypes: NotificationSubCategory[];
  notificationPreferencesSaving: boolean;
  selectNotificationView: (view: NotificationViewRef) => void;
} & FiltersSortContextType<string>;

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

const NOTIFICATIONS_LIMIT = 20;

export enum NOTIFICATION_FIELDS {
  amountUsdCents = "amountUsdCents",
  wealthUsdCents = "wealthUsdCents",
  afterDaysAgo = "afterDaysAgo",
  post = "post",
  user = "user",
  status = "status",
  subcategory = "subcategory",
  category = "category",
  categories = "categories",
  subcategories = "subcategories",
  excludedCategories = "excludedCategories",
  excludedSubcategories = "excludedSubcategories",
  hasRevenue = "hasRevenue",
}

type NotificationsProviderProps = {
  children: ReactNode;
  storageKey?: LocalStorageKeys | false;
  initialFilters?: FilterRecord[];
  initialSort?: SortVariant<string> | null;
  publicKey?: string;
  markUnreadNotificationsAsRead?: boolean;
  showUnreadIndicator?: boolean;
  relativeUsername?: string | null;
  enableNotificationViews?: boolean;
};

export const getNotificationRequestFiltersFromLocalStorage = ({
  filters,
  params = {},
}: {
  filters: FilterRecord[];
  params: GetNotificationParams;
}) => {
  filters.forEach((filter) => {
    const name = filter.name;

    if (
      !(Array.isArray(filter.value) && !filter.value.length) &&
      ![
        NOTIFICATION_FIELDS.amountUsdCents,
        NOTIFICATION_FIELDS.wealthUsdCents,
      ].includes(name as NOTIFICATION_FIELDS)
    ) {
      const isArrayFilter = [
        NOTIFICATION_FIELDS.categories,
        NOTIFICATION_FIELDS.subcategories,
        NOTIFICATION_FIELDS.excludedCategories,
        NOTIFICATION_FIELDS.excludedSubcategories,
      ].includes(name as NOTIFICATION_FIELDS);

      // @ts-expect-error note that this is a hack to make TS happy
      params[name] = isArrayFilter ? filter.value.join(",") : filter.value;
    }

    if (
      name === NOTIFICATION_FIELDS.amountUsdCents ||
      name === NOTIFICATION_FIELDS.wealthUsdCents
    ) {
      const value = filter.value as FilterNumericValue;
      const min = Number(value?.min);
      const max = Number(value?.max);

      if (!Number.isNaN(min)) {
        params[`${name}Gte`] = min * 100;
      }
      if (!Number.isNaN(max)) {
        params[`${name}Lte`] = max * 100;
      }
    }
    return params;
  });

  return params;
};

const getNotificationParamList = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string");
  }

  if (typeof value === "string") {
    return value.split(",").filter(Boolean);
  }

  return [];
};

export const applyHiddenNotificationTypesToParams = ({
  hiddenNotificationTypes,
  params,
}: {
  hiddenNotificationTypes: NotificationSubCategory[];
  params: GetNotificationParams;
}): GetNotificationParams => {
  if (hiddenNotificationTypes.length === 0) {
    return params;
  }

  // Hidden types are a persisted preference, separate from per-view filters, so
  // row-level "Hide Notification Type" survives refreshes and can be restored.
  const excludedSubcategories = Array.from(
    new Set([
      ...getNotificationParamList(params.excludedSubcategories),
      ...hiddenNotificationTypes,
    ]),
  );

  return {
    ...params,
    // The Focus notifications API expects multi-select params as CSV strings.
    excludedSubcategories: excludedSubcategories.join(
      ",",
    ) as unknown as NotificationSubCategory[],
  };
};

const getNotificationOrderByFromSort = (sort: SortVariant<string> | null) => {
  if (!sort) {
    return undefined;
  }

  const name = sort.name as NotificationOrderBySortKey;
  const direction = sort.direction || "asc";

  if (name === NotificationOrderBySortKey.orderBy_time) {
    return direction === "asc"
      ? NotificationOrderBy.OLDEST
      : NotificationOrderBy.NEWEST;
  }

  if (name === NotificationOrderBySortKey.orderBy_value) {
    return direction === "asc"
      ? NotificationOrderBy.HI_VALUE
      : NotificationOrderBy.LO_VALUE;
  }

  return undefined;
};

export const NotificationsProvider = ({
  children,
  storageKey = LocalStorageKeys.notificationsFiltersAndSort,
  initialFilters = [],
  initialSort,
  publicKey,
  markUnreadNotificationsAsRead = true,
  showUnreadIndicator = true,
  relativeUsername = null,
  enableNotificationViews = false,
}: NotificationsProviderProps) => {
  const { currentUserPublicKey, loggedIn } = useContext(UserContext);
  const {
    preferences,
    savePreferences,
    saving: notificationPreferencesSaving,
  } = useFocusPreferences();
  const hiddenNotificationTypes = preferences.hiddenNotificationTypes;
  const finalPublicKey = publicKey ?? currentUserPublicKey;
  const stateFn =
    storageKey === false
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        <T,>(k: string, initial: T) => useState(initial)
      : useLocalStorage;
  const [storedFilters, setStoredFilters] = stateFn(
    `${storageKey}-filters`,
    initialFilters,
  );
  const [storedSort, setStoredSort] = stateFn(
    `${storageKey}-sort`,
    initialSort,
  );

  const {
    setFilters,
    findFilterByName,
    removeFilter,
    filters,
    isSortActive,
    isFilterActive,
    updateFilter,
    currentSort,
    setCurrentSort,
  } = useFiltersAndSort<string>({
    initialFilters: storedFilters,
    initialSort: storedSort,
  });

  const [notifications, setNotifications] = useState<FocusNotification[]>([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [notificationViewUnreadCounts, setNotificationViewUnreadCounts] =
    useState<Record<string, number>>({});
  const requestVersionRef = useRef(0);
  const previousPublicKeyRef = useRef(finalPublicKey);
  const previousActiveNotificationViewSnapshotRef = useRef<string | null>(null);
  const [storedActiveNotificationView, setStoredActiveNotificationView] =
    useLocalStorage(
      LocalStorageKeys.notificationsActiveView,
      serializeNotificationViewRef(createBuiltinNotificationViewRef("all")),
      {
        initializeWithValue: false,
      },
    );
  const [notificationViewStorageHydrated, setNotificationViewStorageHydrated] =
    useState(false);

  const notificationViewItems = useMemo(() => {
    return enableNotificationViews
      ? resolveNotificationViewItems(preferences)
      : [];
  }, [enableNotificationViews, preferences]);
  const storedActiveNotificationViewRef = useMemo(() => {
    return parseNotificationViewRef(storedActiveNotificationView);
  }, [storedActiveNotificationView]);
  const fallbackNotificationViewRef = useMemo(
    () =>
      notificationViewItems[0]?.ref ?? createBuiltinNotificationViewRef("all"),
    [notificationViewItems],
  );
  const activeNotificationViewRef =
    storedActiveNotificationViewRef ?? fallbackNotificationViewRef;
  const activeNotificationView = useMemo(() => {
    if (!enableNotificationViews || !notificationViewStorageHydrated) {
      return null;
    }

    return (
      notificationViewItems.find((view) =>
        areNotificationViewRefsEqual(view.ref, activeNotificationViewRef),
      ) ??
      resolveNotificationViewItem(fallbackNotificationViewRef, preferences) ??
      null
    );
  }, [
    activeNotificationViewRef,
    enableNotificationViews,
    fallbackNotificationViewRef,
    notificationViewItems,
    notificationViewStorageHydrated,
    preferences,
  ]);
  const notificationViewCountQueryKey = useMemo(
    () =>
      notificationViewItems
        .map((view) =>
          JSON.stringify({
            key: view.key,
            filters: view.filters,
            hiddenNotificationTypes: view.hiddenNotificationTypes,
            hiddenNotificationTypesMode: view.hiddenNotificationTypesMode,
          }),
        )
        .join("|"),
    [notificationViewItems],
  );
  const hiddenNotificationTypesKey = useMemo(
    () => hiddenNotificationTypes.join(","),
    [hiddenNotificationTypes],
  );
  const activeNotificationHiddenTypes = useMemo(() => {
    return getNotificationViewHiddenTypes(
      activeNotificationView,
      hiddenNotificationTypes,
    );
  }, [activeNotificationView, hiddenNotificationTypes]);
  const activeNotificationHiddenTypesKey = useMemo(
    () => activeNotificationHiddenTypes.join(","),
    [activeNotificationHiddenTypes],
  );
  const previousHiddenNotificationTypesKeyRef = useRef(
    activeNotificationHiddenTypesKey,
  );
  const activeNotificationViewSnapshot = useMemo(() => {
    if (!activeNotificationView) {
      return null;
    }

    return JSON.stringify({
      filters: activeNotificationView.filters,
      hiddenNotificationTypes: activeNotificationView.hiddenNotificationTypes,
      hiddenNotificationTypesMode:
        activeNotificationView.hiddenNotificationTypesMode,
      key: activeNotificationView.key,
      sort: activeNotificationView.sort,
    });
  }, [activeNotificationView]);

  const getNotifications = useCallback(async () => {
    const requestVersion = requestVersionRef.current;
    const requestOffset = loadedCount;

    const orderBy = getNotificationOrderByFromSort(currentSort);

    let params: GetNotificationParams = {
      limit: NOTIFICATIONS_LIMIT,
      offset: requestOffset,
      publicKey: finalPublicKey,
    };

    if (orderBy) {
      params.orderBy = orderBy;
    }

    params = getNotificationRequestFiltersFromLocalStorage({
      filters,
      params,
    });
    params = applyHiddenNotificationTypesToParams({
      hiddenNotificationTypes: activeNotificationHiddenTypes,
      params,
    });

    try {
      const data = await getNotificationsRequest(params);

      const nextNotifications = data?.Notifications ?? [];

      if (requestVersion !== requestVersionRef.current) {
        return;
      }

      setHasMore(nextNotifications.length === NOTIFICATIONS_LIMIT);
      setLoadedCount(
        data?.NextOffset ?? requestOffset + nextNotifications.length,
      );
      setNotifications((currentNotifications) =>
        mergeNotificationsById(currentNotifications, nextNotifications),
      );
    } catch (error) {
      console.error("Failed to get notifications:", error);
    }
  }, [
    currentSort,
    filters,
    finalPublicKey,
    activeNotificationHiddenTypes,
    loadedCount,
  ]);

  const { loadingMarkup, setInitialLoading, initialLoading } =
    usePaginationRequest({
      initialRequest: getNotifications,
      fetchRequest: getNotifications,
      hasMoreItems: hasMore,
    });

  const resetNotificationList = useCallback(() => {
    requestVersionRef.current += 1;
    setLoadedCount(0);
    setNotifications([]);
    setInitialLoading(true);
    setHasMore(true);
  }, [setInitialLoading]);

  useEffect(() => {
    if (
      previousHiddenNotificationTypesKeyRef.current ===
      activeNotificationHiddenTypesKey
    ) {
      return;
    }

    previousHiddenNotificationTypesKeyRef.current =
      activeNotificationHiddenTypesKey;
    resetNotificationList();
  }, [activeNotificationHiddenTypesKey, resetNotificationList]);

  const { data: fetchedNotificationViewUnreadCounts, refetch } = useQuery({
    queryKey: [
      "notification-view-unread-counts",
      finalPublicKey,
      hiddenNotificationTypesKey,
      notificationViewCountQueryKey,
    ],
    queryFn: async () => {
      const countEntries = await Promise.all(
        notificationViewItems.map(async (view) => {
          let params: GetNotificationParams = {
            limit: 1,
            offset: 0,
            publicKey: finalPublicKey,
            status: NotificationStatus.PROCESSED,
          };

          params = getNotificationRequestFiltersFromLocalStorage({
            filters: view.filters as FilterRecord[],
            params,
          });
          params = applyHiddenNotificationTypesToParams({
            hiddenNotificationTypes: getNotificationViewHiddenTypes(
              view,
              hiddenNotificationTypes,
            ),
            params,
          });

          const data = await getNotificationsRequest(params);

          return [view.key, data.Count] as const;
        }),
      );

      return Object.fromEntries(countEntries);
    },
    enabled:
      enableNotificationViews &&
      notificationViewItems.length > 0 &&
      !!finalPublicKey,
    placeholderData: {},
  });

  useEffect(() => {
    if (!enableNotificationViews || !fetchedNotificationViewUnreadCounts) {
      return;
    }

    startTransition(() => {
      setNotificationViewUnreadCounts(fetchedNotificationViewUnreadCounts);
    });
  }, [enableNotificationViews, fetchedNotificationViewUnreadCounts]);

  const refreshNotificationViewCounts = useCallback(() => {
    if (!enableNotificationViews) {
      return;
    }

    void refetch();
  }, [enableNotificationViews, refetch]);

  const onFiltersApply = useCallback(() => {
    resetNotificationList();
  }, [resetNotificationList]);

  const resetNotificationFilters = useCallback(() => {
    setFilters(initialFilters, true);
    resetNotificationList();
  }, [initialFilters, resetNotificationList, setFilters]);

  const resetNotificationSort = useCallback(() => {
    setCurrentSort(initialSort ?? null, true);
    resetNotificationList();
  }, [initialSort, resetNotificationList, setCurrentSort]);

  const isNotificationFilterActive = useCallback(
    <T extends string>(name: T) => {
      if (filters.length === 0) return isFilterActive(name);

      const filter = filters.find((f) => f.name === name);
      const initialFilter = initialFilters.find((f) => f.name === name);

      return !isEqual(filter, initialFilter);
    },
    [filters, initialFilters, isFilterActive],
  );

  const resetAll = useCallback(() => {
    setFilters([], true);
    setCurrentSort(null, true);
    resetNotificationList();
  }, [resetNotificationList, setCurrentSort, setFilters]);

  useEffect(() => {
    setStoredFilters(filters);
    setStoredSort(currentSort);
  }, [filters, currentSort]);

  useEffect(() => {
    setNotificationViewStorageHydrated(true);
  }, []);

  useEffect(() => {
    if (
      !enableNotificationViews ||
      !notificationViewStorageHydrated ||
      !activeNotificationView
    ) {
      return;
    }

    if (
      !areNotificationViewRefsEqual(
        storedActiveNotificationViewRef,
        activeNotificationView.ref,
      )
    ) {
      setStoredActiveNotificationView(
        serializeNotificationViewRef(activeNotificationView.ref),
      );
    }

    if (
      previousActiveNotificationViewSnapshotRef.current ===
      activeNotificationViewSnapshot
    ) {
      return;
    }

    previousActiveNotificationViewSnapshotRef.current =
      activeNotificationViewSnapshot;

    setFilters(activeNotificationView.filters as FilterRecord[], true);
    setCurrentSort(
      activeNotificationView.sort as SortVariant<string> | null,
      true,
    );
    resetNotificationList();
  }, [
    activeNotificationView,
    activeNotificationViewSnapshot,
    enableNotificationViews,
    notificationViewStorageHydrated,
    resetNotificationList,
    setCurrentSort,
    setFilters,
    setStoredActiveNotificationView,
    storedActiveNotificationViewRef,
  ]);

  useEffect(() => {
    if (previousPublicKeyRef.current === finalPublicKey) {
      return;
    }

    previousPublicKeyRef.current = finalPublicKey;

    if (finalPublicKey) {
      resetNotificationList();
    }
  }, [finalPublicKey, resetNotificationList]);

  const refreshNotifications = useCallback(() => {
    resetNotificationList();
    refreshNotificationViewCounts();
  }, [refreshNotificationViewCounts, resetNotificationList]);

  const hideNotificationType = useCallback(
    async (notificationType: NotificationSubCategory) => {
      if (!loggedIn) {
        throw new Error(
          "A logged-in user is required to hide notification types.",
        );
      }

      if (hiddenNotificationTypes.includes(notificationType)) {
        return;
      }

      const nextHiddenNotificationTypes = Array.from(
        new Set([...hiddenNotificationTypes, notificationType]),
      );

      await savePreferences(
        {
          ...preferences,
          hiddenNotificationTypes: nextHiddenNotificationTypes,
        },
        {
          loggedIn,
        },
      );

      setNotifications((currentNotifications) =>
        currentNotifications.filter(
          (entry) => entry.Notification.Subcategory !== notificationType,
        ),
      );
    },
    [hiddenNotificationTypes, loggedIn, preferences, savePreferences],
  );

  const selectNotificationView = useCallback(
    (view: NotificationViewRef) => {
      setStoredActiveNotificationView(serializeNotificationViewRef(view));
    },
    [setStoredActiveNotificationView],
  );

  const decrementNotificationViewUnreadCounts = useCallback(
    (notification: FocusNotification) => {
      if (!enableNotificationViews) {
        return;
      }

      setNotificationViewUnreadCounts((currentCounts) => {
        return notificationViewItems.reduce<Record<string, number>>(
          (nextCounts, view) => {
            if (
              notificationMatchesUnreadView(
                notification,
                view,
                hiddenNotificationTypes,
              )
            ) {
              nextCounts[view.key] = Math.max(
                0,
                (nextCounts[view.key] ?? 0) - 1,
              );
            }

            return nextCounts;
          },
          { ...currentCounts },
        );
      });
    },
    [enableNotificationViews, hiddenNotificationTypes, notificationViewItems],
  );

  const markAllNotificationViewCountsAsRead = useCallback(() => {
    setNotificationViewUnreadCounts((currentCounts) =>
      Object.fromEntries(Object.keys(currentCounts).map((key) => [key, 0])),
    );
  }, []);

  return (
    <NotificationsContext.Provider
      value={{
        resetAll,
        initialLoading,
        onFiltersApply,
        lazyLoadingMarkup: loadingMarkup,
        notifications,
        setFilters,
        findFilterByName,
        removeFilter,
        filters,
        isSortActive,
        isFilterActive: isNotificationFilterActive,
        updateFilter,
        resetNotificationFilters,
        resetNotificationSort,
        setNotifications,
        currentSort,
        setCurrentSort,
        notificationPublicKey: finalPublicKey,
        markUnreadNotificationsAsRead,
        showUnreadIndicator,
        relativeUsername,
        activeNotificationView,
        decrementNotificationViewUnreadCounts,
        markAllNotificationViewCountsAsRead,
        notificationViewItems,
        notificationViewUnreadCounts,
        notificationViewsEnabled: enableNotificationViews,
        refreshNotificationViewCounts,
        refreshNotifications,
        hideNotificationType,
        hiddenNotificationTypes,
        notificationPreferencesSaving,
        selectNotificationView,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsContext.Provider",
    );
  }
  return context;
};
